import { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import Select from "react-select";
import Pagination from "@mui/material/Pagination";
import { authContext } from "../contexts/AuthProvider";
import { loaderContext } from "../contexts/LoaderProvider";
import { branchSwitchContext } from "../contexts/BranchSwitchProvider";
import Loader from "../layout/Loader";
import Header from "../layout/Header";
import Sidebar from "../layout/Sidebar";
import Footer from "../layout/Footer";
import ScrollToTop from "../layout/ScrollToTop";
import LoadScript from "../utils/LoadScript";
import {
  fetchEmployees,
  createNewEmployee,
  editEmployee,
  resetEmployeePassword,
  archiveEmployee,
  restoreEmployee,
} from "../services/employeeService";
import { branchList } from "../services/branchService";
import { dataContext } from "../contexts/DataProvider";
import { errorContext } from "../contexts/ErrorProvider";
import ProcessData from "../utils/ProcessData";
import validate from "../validation/validator";
import { employeeSchema } from "../validation/schema";

const AllEmployees = () => {
  const [data, setData] = useContext(dataContext);
  const [errors, setErrors] = useContext(errorContext);
  const { branchSwitch } = useContext(branchSwitchContext);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [modal, setModal] = useState({ open: false, action: "", data: "" });
  const { isLoading, setIsLoading } = useContext(loaderContext);
  const [isAuth] = useContext(authContext);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const fetchBranchList = useCallback(async () => {
    const response = await branchList();

    if (response.data.code === 1) {
      setBranches(response?.data?.data?.branchList);
    }
  }, []);

  const getEmployees = useCallback(async () => {
    setIsLoading(true);
    const response = await fetchEmployees(page, limit, search);
    if (response.data.code === 1) {
      setEmployees(response.data.data);
      setTotalPages(response.data.pages);
      setIsLoading(false);
    }
  }, [page, search, limit, setIsLoading]);

  const getBranches = useCallback(async () => {
    await fetchBranchList();
  }, [fetchBranchList]);

  const add = useCallback(async () => {
    const validation = validate(data, setErrors, employeeSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await createNewEmployee(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getEmployees();
      } else {
        setErrors({ ...errors, email: response.data.message });
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);
  const update = useCallback(async () => {
    const validation = validate(data, setErrors, employeeSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await editEmployee(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getEmployees();
      } else {
        setErrors({ ...errors, email: response.data.message });
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);
  const resetPassword = useCallback(async () => {
    const validation = validate(data, setErrors, employeeSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await resetEmployeePassword(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getEmployees();
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);
  const archive = useCallback(async () => {
    const validation = validate(data, setErrors, employeeSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await archiveEmployee(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getEmployees();
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);
  const restore = useCallback(async () => {
    const validation = validate(data, setErrors, employeeSchema());
    if (validation === true) {
      setIsLoading(true);
      const response = await restoreEmployee(data);
      if (response.data.code === 1) {
        setModal({ open: false, action: "", data: "" });
        setData({});
        setErrors({});
        getEmployees();
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [data, errors]);

  useEffect(() => {
    if (isAuth === true) {
      setIsLoading(false);
    }
    if (isAuth === false) {
      navigate("/");
    }
  }, [isAuth, navigate, setIsLoading]);

  useEffect(() => {
    getBranches();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getEmployees();
  }, [page, search, getEmployees, branchSwitch]);

  const renderEmployeeList = (employees) => (
    <>
      {isLoading === true ? (
        <Loader />
      ) : (
        <>
          {employees?.length > 0 ? (
            <div className="table-responsive">
              <table className="table text-nowrap">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Role</th>
                    <th scope="col">Status</th>
                    <th scope="col">Member Since</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {employees?.map((employee, index) => (
                    <tr key={index}>
                      <td>{employee?.name}</td>
                      <td>{employee?.email}</td>
                      <td>{employee?.role}</td>
                      <td>
                        <span
                          className={`badge bg-${
                            employee?.status === true ? "success" : "danger"
                          }-transparent`}
                        >
                          {employee?.status === true ? "Active" : "Suspended"}
                        </span>
                      </td>
                      <td>
                        {moment(employee?.createdAt)
                          .tz(process.env.REACT_APP_TIMEZONE)
                          .format("Do MMM, YYYY")}
                      </td>
                      <td>
                        <div className="btn-list">
                          <button
                            onClick={() => {
                              setData(employee);
                              setModal({
                                open: true,
                                action: "edit",
                                data: "",
                              });
                            }}
                            title="Edit"
                            className="btn btn-sm btn-icon btn-primary-light btn-wave rounded-circle waves-effect waves-light"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => {
                              setData(employee);
                              setModal({
                                open: true,
                                action: "reset-password",
                                data: "",
                              });
                            }}
                            title="Reset Password"
                            className="btn btn-sm btn-icon btn-warning-light btn-wave rounded-circle waves-effect waves-light"
                          >
                            <i className="ri-key-line"></i>
                          </button>
                          {/* {employee?.isDeleted === true ? (
                            <button
                              onClick={() => {
                                setData(employee);
                                setModal({
                                  open: true,
                                  action: "restore",
                                  data: "",
                                });
                              }}
                              title="Restore"
                              className="btn btn-sm btn-icon btn-success-light btn-wave rounded-circle waves-effect waves-light"
                            >
                              <i className="ri-restart-line"></i>
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setData(employee);
                                setModal({
                                  open: true,
                                  action: "archive",
                                  data: "",
                                });
                              }}
                              title="Archive"
                              className="btn btn-sm btn-icon btn-danger-light btn-wave rounded-circle waves-effect waves-light"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )} */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="6">
                      <div className="d-flex justify-content-end">
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, value) => setPage(value)}
                        />
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center my-5">
              <img
                className="mx-auto w-25 my-5"
                src={`${process.env.REACT_APP_PUBLIC_URL}assets/images/media/backgrounds/no-content.png`}
                alt="no-content"
              />
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <div className="page">
        <Header />
        <Sidebar />
        <div className="main-content app-content">
          <div className="container-fluid">
            <div className="row mt-4">
              <div className="col-md-12">
                <div className="card custom-card">
                  <div className="card-header justify-content-between">
                    <div className="card-title">
                      <div className="d-flex justify-content-between align-items-center px-3 py- gap-2">
                        <span className="d-block fw-medium fs-15">
                          Manage Employees
                        </span>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <div>
                        <input
                          className="form-control form-control-md"
                          type="text"
                          placeholder="Search Here"
                          aria-label=".form-control-sm example"
                          onChange={(e) => setSearch(e.target.value)}
                          value={search}
                        />
                      </div>
                      <div>
                        <button
                          onClick={() => {
                            setModal({
                              open: true,
                              action: "add",
                              data: "",
                            });
                          }}
                          className="btn btn-primary btn-md me-2"
                        >
                          <i className="ri-add-line me-1 fw-medium align-middle"></i>
                          New Employee
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {renderEmployeeList(employees)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Begin:Add Employee */}
          <div
            className={
              modal?.open === true && modal?.action === "add"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "add"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-md"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Add Employee</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <input
                        className="form-control form-control-md"
                        type="text"
                        placeholder="Enter Name"
                        autoComplete="off"
                        value={data?.name ?? ""}
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "string",
                            "name",
                            errors,
                            setErrors
                          );
                        }}
                      />
                      <small className="text-danger">
                        {errors?.name ?? ""}
                      </small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <input
                        className="form-control form-control-md"
                        type="text"
                        placeholder="Enter Email Address"
                        autoComplete="off"
                        value={data?.email ?? ""}
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "string",
                            "email",
                            errors,
                            setErrors
                          );
                        }}
                      />
                      <small className="text-danger">
                        {errors?.email ?? ""}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <Select
                        className="react-select-styled react-select-bordered react-select-sm"
                        classNamePrefix="react-select"
                        value={
                          data?.role
                            ? {
                                label: data?.role,
                                value: data?.role,
                              }
                            : null
                        }
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "select",
                            "role",
                            errors,
                            setErrors
                          );
                        }}
                        options={[
                          {
                            label: "Employee",
                            value: "Employee",
                          },

                          {
                            label: "Owner",
                            value: "Owner",
                          },
                          {
                            label: "Manager",
                            value: "Manager",
                          },
                          {
                            label: "Accuntant",
                            value: "Accuntant",
                          },
                        ]}
                        placeholder="Select Role"
                      />
                      <small className="text-danger">
                        {errors?.role ?? ""}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <Select
                        className="react-select-styled react-select-bordered react-select-sm"
                        classNamePrefix="react-select"
                        value={branches
                          ?.map((branch) => ({
                            label: branch?.name,
                            value: branch?._id,
                          }))
                          .find(
                            (branch) => branch?.value === data?.branchId ?? null
                          )}
                        options={branches?.map((branch) => {
                          return {
                            label: branch?.name,
                            value: branch?._id,
                          };
                        })}
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "select",
                            "branchId",
                            errors,
                            setErrors
                          );
                        }}
                        placeholder="Select Branch"
                      />
                      <small className="text-danger">
                        {errors?.branchId ?? ""}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      Close
                    </button>
                    <button
                      onClick={add}
                      className="btn btn-primary"
                      type="button"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Add Employee */}
          {/* Begin:Edit Employee */}
          <div
            className={
              modal?.open === true && modal?.action === "edit"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "edit"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-md"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Edit Employee</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <input
                        className="form-control form-control-md"
                        type="text"
                        placeholder="Enter Name"
                        autoComplete="off"
                        value={data?.name ?? ""}
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "string",
                            "name",
                            errors,
                            setErrors
                          );
                        }}
                      />
                      <small className="text-danger">
                        {errors?.name ?? ""}
                      </small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <input
                        className="form-control form-control-md"
                        type="text"
                        placeholder="Enter Email Address"
                        autoComplete="off"
                        value={data?.email ?? ""}
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "string",
                            "email",
                            errors,
                            setErrors
                          );
                        }}
                      />
                      <small className="text-danger">
                        {errors?.email ?? ""}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <Select
                        className="react-select-styled react-select-bordered react-select-sm"
                        classNamePrefix="react-select"
                        value={
                          data?.role
                            ? {
                                label: data?.role,
                                value: data?.role,
                              }
                            : null
                        }
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "select",
                            "role",
                            errors,
                            setErrors
                          );
                        }}
                        options={[
                          {
                            label: "Employee",
                            value: "Employee",
                          },

                          {
                            label: "Owner",
                            value: "Owner",
                          },
                          {
                            label: "Manager",
                            value: "Manager",
                          },
                          {
                            label: "Accuntant",
                            value: "Accuntant",
                          },
                        ]}
                        placeholder="Select Role"
                      />
                      <small className="text-danger">
                        {errors?.role ?? ""}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <Select
                        className="react-select-styled react-select-bordered react-select-sm"
                        classNamePrefix="react-select"
                        value={branches
                          ?.map((branch) => ({
                            label: branch?.name,
                            value: branch?._id,
                          }))
                          .find(
                            (branch) => branch?.value === data?.branchId ?? null
                          )}
                        options={branches?.map((branch) => {
                          return {
                            label: branch?.name,
                            value: branch?._id,
                          };
                        })}
                        onChange={(e) => {
                          ProcessData(
                            data,
                            setData,
                            e,
                            "select",
                            "branchId",
                            errors,
                            setErrors
                          );
                        }}
                        placeholder="Select Branch"
                      />
                      <small className="text-danger">
                        {errors?.branchId ?? ""}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      Close
                    </button>
                    <button
                      onClick={update}
                      className="btn btn-primary"
                      type="button"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Edit Employee */}
          {/* Begin:Reset Password */}
          <div
            className={
              modal?.open === true && modal?.action === "reset-password"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "reset-password"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-sm"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Reset Password</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <h5>Are You Sure?</h5>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="w-100 d-flex justify-content-center gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      No
                    </button>
                    <button
                      onClick={resetPassword}
                      className="btn btn-primary"
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Reset Password */}
          {/* Begin:Archive */}
          <div
            className={
              modal?.open === true && modal?.action === "archive"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "archive"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-sm"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Archive Employee</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <h5>Are You Sure?</h5>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="w-100 d-flex justify-content-center gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      No
                    </button>
                    <button
                      onClick={archive}
                      className="btn btn-primary"
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Archive */}
          {/* Begin:Restore */}
          <div
            className={
              modal?.open === true && modal?.action === "restore"
                ? "modal fade effect-slide-in-bottom show bg-success-transparent"
                : "modal fade effect-slide-in-bottom"
            }
            style={
              modal?.open === true && modal?.action === "restore"
                ? { display: "block" }
                : {}
            }
          >
            <div
              className="modal-dialog modal-dialog-centered modal-sm"
              role="document"
            >
              <div className="modal-content modal-content-demo">
                <div className="modal-header">
                  <h6 className="modal-title">Restore Employee</h6>
                  <button
                    onClick={() => {
                      setModal({ open: false, action: "", data: "" });
                      setData({});
                      setErrors({});
                    }}
                    aria-label="Close"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <h5>Are You Sure?</h5>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="w-100 d-flex justify-content-center gap-2">
                    <button
                      onClick={() => {
                        setModal({ open: false, action: "", data: "" });
                        setData({});
                        setErrors({});
                      }}
                      className="btn btn-danger"
                      type="button"
                    >
                      No
                    </button>
                    <button
                      onClick={restore}
                      className="btn btn-primary"
                      type="button"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End:Restore */}
        </div>
        <Footer />
      </div>
      <ScrollToTop />
      <LoadScript />
    </>
  );
};

export default AllEmployees;
